import {
  Pagination as PaginationPrimitive,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink as PaginationLinkPrimitive,
  PaginationNext as PaginationNextPrimitive,
  PaginationPrevious as PaginationPreviousPrimitive,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slottable,
} from "@design-system/shadcn";
import type { DataAttributes } from "@design-system/utils";
import React, { createContext, useContext } from "react";

import { ELLIPSIS, generatePageNumbers } from "../hooks";
import {
  type PaginationMeta,
  PAGE_SIZE_OPTIONS,
  type PageSizeOptions,
} from "../schemas";

interface PaginationContextValue {
  pagination: { pageIndex: number; pageSize: number };
  meta?: PaginationMeta;
  onPageSizeChange: (pageSize: number) => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pageSizeOptions?: PageSizeOptions;
}

const PaginationContext = createContext<PaginationContextValue | null>(null);

function usePaginationContext() {
  const ctx = useContext(PaginationContext);
  if (!ctx) {
    throw new Error(
      "Pagination sub-components must be rendered inside <Pagination>"
    );
  }
  return ctx;
}

interface PaginationRootProps extends PaginationContextValue {
  children: React.ReactNode;
}

function PaginationRoot({ children, ...ctx }: PaginationRootProps) {
  return (
    <PaginationContext.Provider value={ctx}>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-4 py-4">
        {children}
      </div>
    </PaginationContext.Provider>
  );
}

function PaginationPageSize(props: DataAttributes) {
  const { pagination, onPageSizeChange, pageSizeOptions } =
    usePaginationContext();
  return (
    <Select
      value={`${pagination.pageSize}`}
      onValueChange={(value) => onPageSizeChange(Number(value))}
    >
      <SelectTrigger
        {...props}
        className="w-[70px] flex-shrink-0 *:data-[slot=select-value]:line-clamp-none"
      >
        <SelectValue placeholder={pagination.pageSize} />
      </SelectTrigger>
      <SelectContent side="top">
        {(pageSizeOptions ?? PAGE_SIZE_OPTIONS).map((size) => (
          <SelectItem key={size} {...props} value={`${size}`}>
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface PaginationTotalProps {
  children: (count: number) => React.ReactNode;
}

function PaginationTotal({ children }: PaginationTotalProps) {
  const { meta } = usePaginationContext();
  return (
    <div className="grow text-sm font-medium whitespace-nowrap">
      {children(meta?.totalCount ?? 0)}
    </div>
  );
}

interface PaginationNavProps {
  children: React.ReactNode;
}

function PaginationNav({ children }: PaginationNavProps) {
  return (
    <div className="ml-auto flex-shrink-0">
      <PaginationPrimitive>
        <PaginationContent>{children}</PaginationContent>
      </PaginationPrimitive>
    </div>
  );
}

interface PaginationStepProps {
  label: string;
  children: React.ReactElement;
}

function PaginationPrevious({ label, children }: PaginationStepProps) {
  const { canPreviousPage } = usePaginationContext();
  return (
    <PaginationItem>
      {canPreviousPage ? (
        <PaginationPreviousPrimitive asChild label={label} aria-label={label}>
          {children}
        </PaginationPreviousPrimitive>
      ) : (
        <PaginationPreviousPrimitive
          asChild
          label={label}
          aria-label={label}
          className="pointer-events-none opacity-50"
        >
          <span role="link" aria-disabled="true" />
        </PaginationPreviousPrimitive>
      )}
    </PaginationItem>
  );
}

function PaginationNext({ label, children }: PaginationStepProps) {
  const { canNextPage } = usePaginationContext();
  return (
    <PaginationItem>
      {canNextPage ? (
        <PaginationNextPrimitive asChild label={label} aria-label={label}>
          {children}
        </PaginationNextPrimitive>
      ) : (
        <PaginationNextPrimitive
          asChild
          label={label}
          aria-label={label}
          className="pointer-events-none opacity-50"
        >
          <span role="link" aria-disabled="true" />
        </PaginationNextPrimitive>
      )}
    </PaginationItem>
  );
}

interface PaginationPagesRenderProps {
  pageIndex: number;
  isActive: boolean;
}

interface PaginationPagesProps {
  children: (props: PaginationPagesRenderProps) => React.ReactElement;
}

function PaginationPages({ children: renderLink }: PaginationPagesProps) {
  const { pagination, meta } = usePaginationContext();
  const totalPages = meta?.totalPages ?? 0;
  if (totalPages <= 1) return null;

  const currentPage = pagination.pageIndex + 1;
  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  return (
    <>
      {pageNumbers.map((page, index) => {
        if (page === ELLIPSIS) {
          return (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          );
        }
        const isActive = page === currentPage;
        return (
          <PaginationItem key={page}>
            <PaginationLinkPrimitive asChild isActive={isActive}>
              <Slottable>
                {renderLink({ pageIndex: page - 1, isActive })}
              </Slottable>
              {page}
            </PaginationLinkPrimitive>
          </PaginationItem>
        );
      })}
    </>
  );
}

PaginationRoot.PageSize = PaginationPageSize;
PaginationRoot.Total = PaginationTotal;
PaginationRoot.Nav = PaginationNav;
PaginationRoot.Previous = PaginationPrevious;
PaginationRoot.Pages = PaginationPages;
PaginationRoot.Next = PaginationNext;

export { PaginationRoot as Pagination };
export type {
  PaginationRootProps,
  PaginationTotalProps,
  PaginationNavProps,
  PaginationStepProps,
  PaginationPagesProps,
  PaginationPagesRenderProps,
};
